import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import {
  CreditCard,
  File,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import PaymentProofUpload from '../../components/brand/PaymentProofUpload';
import paymentProofService from '../../services/paymentProofService';

const Billing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    fetchCampaignPayments();
  }, [currentUser]);
  
  const fetchCampaignPayments = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get all campaigns by this brand that have payment information
      const brandPayments = await paymentProofService.getBrandPayments(currentUser.uid);
      setCampaigns(brandPayments);
    } catch (err) {
      console.error('Error fetching campaign payments:', err);
      setError('Failed to load billing information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUploadClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowUploadModal(true);
  };
  
  const handleViewProof = (campaign) => {
    setSelectedCampaign(campaign);
    setShowProofModal(true);
  };
  
  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchCampaignPayments();
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return format(date, 'MMM dd, yyyy h:mm a');
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_payment':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="mr-1 h-3 w-3" />
            Payment Required
          </span>
        );
      case 'proof_submitted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="mr-1 h-3 w-3" />
            Proof Submitted
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Payment Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="mr-1 h-3 w-3" />
            Payment Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  const generateReferenceCode = (campaignId) => {
    return paymentProofService.generateReferenceCode(campaignId);
  };
  
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Campaign Billing</h2>
        <p className="text-gray-600">Manage your campaign budget payments and upload payment proofs.</p>
      </div>
      
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" color="primary" />
            </div>
          ) : campaigns.length > 0 ? (
            <div className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Type</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference Code</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {campaign.campaignName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(campaign.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Initial Payment (70%)
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getStatusBadge(campaign.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.referenceCode || generateReferenceCode(campaign.id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(campaign.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {campaign.status === 'pending_payment' ? (
                            <button
                              onClick={() => handleUploadClick(campaign)}
                              className="text-primary hover:text-primary-dark flex items-center"
                            >
                              Upload Proof
                            </button>
                          ) : campaign.proofUrl ? (
                            <button
                              onClick={() => handleViewProof(campaign)}
                              className="text-primary hover:text-primary-dark flex items-center"
                            >
                              View Proof
                            </button>
                          ) : (
                            <span className="text-gray-400">No action needed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No campaign billing information found.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Information */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Campaign Budget Payment Process</h3>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-2">How Campaign Budget Payments Work</h4>
            <p className="text-sm text-gray-600">
              When you create a campaign, an initial payment of 70% of your campaign budget is required before your campaign becomes active and visible to influencers. The remaining 30% is paid after campaign completion. The process works as follows:
            </p>
            <ol className="mt-2 list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Create your campaign and submit it</li>
              <li>Pay the initial amount (70% of budget) to the provided bank account</li>
              <li>Upload your payment proof (receipt, transfer confirmation, etc.)</li>
              <li>Admin verifies your payment</li>
              <li>Once verified, your campaign becomes active and visible to influencers</li>
            </ol>
          </div>
          
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-2">Payment Methods</h4>
            <p className="text-sm text-gray-600 mb-4">
              We currently accept the following payment methods for campaign budget payments:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">BT</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">Bank Transfer</h5>
                  <p className="text-xs text-gray-500">1-2 business days for verification</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">GC</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">GCash</h5>
                  <p className="text-xs text-gray-500">Same-day verification</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-bold">MP</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">Maya</h5>
                  <p className="text-xs text-gray-500">Same-day verification</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upload Payment Proof Modal */}
      {showUploadModal && selectedCampaign && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary">
                  <CreditCard className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Upload Payment Proof
                  </h3>
                  
                  <div className="mt-4">
                    <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-4">
                      <p className="text-blue-800 font-medium">
                        Campaign: {selectedCampaign.campaignName}
                      </p>
                      <p className="text-blue-800 mt-1">
                        Commission Amount: {formatCurrency(selectedCampaign.amount)}
                      </p>
                      <p className="text-blue-800 mt-1">
                        Reference Code: {selectedCampaign.referenceCode || generateReferenceCode(selectedCampaign.id)}
                      </p>
                    </div>
                    
                    <PaymentProofUpload 
                      campaignId={selectedCampaign.id}
                      referenceCode={selectedCampaign.referenceCode || generateReferenceCode(selectedCampaign.id)}
                      onUploadSuccess={handleUploadSuccess}
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* View Payment Proof Modal */}
      {showProofModal && selectedCampaign && selectedCampaign.proofUrl && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary">
                  <File className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Payment Proof
                  </h3>
                  
                  <div className="mt-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
                      <p className="text-gray-800 font-medium">
                        Campaign: {selectedCampaign.campaignName}
                      </p>
                      <p className="text-gray-800 mt-1">
                        Payment Amount: {formatCurrency(selectedCampaign.amount)}
                      </p>
                      <p className="text-gray-800 mt-1">
                        Status: {getStatusBadge(selectedCampaign.status)}
                      </p>
                      <p className="text-gray-800 mt-1">
                        Uploaded: {formatDate(selectedCampaign.proofUploadedAt)}
                      </p>
                    </div>
                    
                    <div className="mt-4 flex justify-center">
                      {selectedCampaign.proofUrl.toLowerCase().endsWith('.pdf') ? (
                        <div className="text-center">
                          <File className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                          <a 
                            href={selectedCampaign.proofUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark"
                          >
                            View PDF Document
                          </a>
                        </div>
                      ) : (
                        <img 
                          src={selectedCampaign.proofUrl} 
                          alt="Payment proof" 
                          className="max-h-96 rounded-md"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  onClick={() => setShowProofModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
